import { useState, useEffect } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { MessageSquare, Mail } from 'lucide-react'
import { type Contact } from '@/data/mockOrdersData'
import { cn } from '@/lib/utils'

interface ChannelSelectorProps {
	contact: Contact
	currentChannel: string
	onChannelChange: (channel: string) => void
	className?: string
}

const getChannelIcon = (channel: string, forButton: boolean = false) => {
	// Use white/light for button, normal text color for dropdown
	const iconClass = forButton ? "h-4 w-4 text-primary-foreground" : "h-4 w-4"
	
	switch (channel) {
		case 'sms':
			return <MessageSquare className={iconClass} />
		case 'email':
			return <Mail className={iconClass} />
		case 'facebook':
			// Use Facebook brand icon from public/brands with theme color
			return (
				<svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
					<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
				</svg>
			)
		default:
			return <MessageSquare className={iconClass} />
	}
}

const getChannelLabel = (channel: string, contact: Contact) => {
	switch (channel) {
		case 'sms':
			return `SMS ${contact.phone_raw || ''}`
		case 'email':
			return contact.email || 'Email'
		case 'facebook':
			return 'Facebook Messenger'
		default:
			return channel
	}
}

const getAvailableChannels = (contact: Contact): Array<{channel: string, available: boolean}> => {
	return [
		{ channel: 'sms', available: !!contact.phone_raw },
		{ channel: 'email', available: !!contact.email },
		{ channel: 'facebook', available: !!contact.facebook_id },
	]
}

export function ChannelSelector({ contact, currentChannel, onChannelChange, className }: ChannelSelectorProps) {
	const availableChannels = getAvailableChannels(contact)
	const [hoveredChannels, setHoveredChannels] = useState<Set<string>>(new Set())
	
	// Reset hovered state when current channel changes
	useEffect(() => {
		setHoveredChannels(new Set())
	}, [currentChannel])
	
	const handleMouseEnter = (channel: string) => {
		// Only track hover for selected channel
		if (channel === currentChannel) {
			setHoveredChannels(prev => new Set(prev).add(channel))
		}
	}

	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<Select value={currentChannel} onValueChange={onChannelChange}>
						<SelectTrigger className={cn(
							// Match Button primary variant styling exactly
							"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium",
							"transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
							"disabled:pointer-events-none disabled:opacity-50",
							"bg-primary text-primary-foreground hover:bg-primary/90",
							"h-9 w-9 p-0",
							// Hide Select-specific elements
							"[&>span]:hidden [&>svg[data-radix-select-icon]]:hidden [&>.lucide-chevron-down]:hidden",
							className
						)}>
							{getChannelIcon(currentChannel, true)}
						</SelectTrigger>
						<SelectContent align="end" className="z-[110]">
							{availableChannels.map(({ channel, available }) => {
								const isSelected = channel === currentChannel
								const hasBeenHovered = hoveredChannels.has(channel)
								const isSelectedHighlighted = isSelected && !hasBeenHovered
								
								return (
									<SelectItem 
										key={channel} 
										value={channel}
										disabled={!available}
										onMouseEnter={() => handleMouseEnter(channel)}
										className={cn(
											"flex items-center gap-2",
											!available && "opacity-50 cursor-not-allowed",
											// Normal hover state for non-selected items
											"hover:text-primary-foreground [&:hover_svg]:text-primary-foreground",
											// Selected with purple background (before hover)
											isSelectedHighlighted && "bg-primary text-primary-foreground [&_svg]:!text-primary-foreground [&_div_svg]:!text-primary-foreground",
											// Selected after hover (no purple background, normal text)
											isSelected && hasBeenHovered && "text-foreground [&_svg]:text-foreground"
										)}
									>
									<div className="flex items-center gap-2">
										{getChannelIcon(channel)}
										<span>{getChannelLabel(channel, contact)}</span>
									</div>
								</SelectItem>
								)
							})}
						</SelectContent>
					</Select>
				</TooltipTrigger>
				<TooltipContent>
					<p>Click to change channels</p>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	)
}
